import { Button } from "@/components/ui/button";
import { formatCurrency, formatTimeShort } from "@/lib/format";
import type { FiscalDay, FiscalRow } from "@/types";
import { METHOD_LABEL, SOURCE_TOTAL_LABEL, rowKey } from "./fiscal-labels";
import { AmountCell, FiscalCheck, FlagBadge, MissingConfirmationBadge, MetaLine, RowActions, type RowHandlers } from "./FiscalRowParts";

interface Props extends RowHandlers {
  day: FiscalDay;
  rows: FiscalRow[];
}

function Summary({ day }: { day: FiscalDay }) {
  const t = day.totals;
  const methods = (Object.keys(METHOD_LABEL) as FiscalRow["method"][]).filter((m) => t.byMethod[m] > 0);
  const sources = (Object.keys(SOURCE_TOTAL_LABEL) as FiscalRow["source"][]).filter((s) => t.bySource[s] > 0);
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-border/50 px-4 py-3 text-sm">
      <div className="text-muted-foreground">
        Ukupno <span className="font-medium text-foreground">{formatCurrency(t.total)}</span>
        {methods.map((m) => (
          <span key={m}>
            {" · "}
            {METHOD_LABEL[m]} {formatCurrency(t.byMethod[m])}
          </span>
        ))}
        {sources.length > 0 && (
          <div>
            {sources.map((s, i) => (
              <span key={s}>
                {i > 0 && " · "}
                {SOURCE_TOTAL_LABEL[s]} {formatCurrency(t.bySource[s])}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="text-muted-foreground">
        Osnovica {formatCurrency(t.base)} · PDV {formatCurrency(t.vat)}
      </div>
      <div className="font-medium text-foreground">
        {t.fiscalizedCount} od {t.count} fiskalizovano
      </div>
    </div>
  );
}

export function FiscalDayTable({ day, rows, onToggle, onResend }: Props) {
  if (day.rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border/60 bg-card py-12 text-center text-sm text-muted-foreground">
        Nema uplata za ovaj dan.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border/60 bg-card py-12 text-center text-sm text-muted-foreground">
        Nema uplata ove vrste za ovaj dan.
      </div>
    );
  }
  // Samo vidljivi redovi: skriveni (filtrirani) redovi se nikad ne označavaju.
  const pending = rows.filter((r) => r.fiscalizedAt === null);

  return (
    <div className="space-y-3">
      {pending.length > 0 && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggle(pending.map((r) => ({ source: r.source, sourceId: r.sourceId })), true)}
          >
            {`Označi sve (${pending.length})`}
          </Button>
        </div>
      )}
      <div className="rounded-2xl border border-border/60 bg-card">
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-10 px-4 py-3" />
                <th className="px-4 py-3">Vrijeme</th>
                <th className="px-4 py-3">Vrsta</th>
                <th className="px-4 py-3">Kupac</th>
                <th className="px-4 py-3">Opis</th>
                <th className="px-4 py-3">Broj</th>
                <th className="px-4 py-3">Način</th>
                <th className="px-4 py-3">Iznos</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={rowKey(r)} className="border-t border-border/50 align-top">
                  <td className="px-4 py-3">
                    <FiscalCheck row={r} onToggle={onToggle} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{formatTimeShort(r.at)}</td>
                  <td className="px-4 py-3">{r.kindLabel}</td>
                  <td className="px-4 py-3">{r.buyer}</td>
                  <td className="max-w-[220px] truncate px-4 py-3 text-muted-foreground" title={r.description}>
                    {r.description}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span>{r.number}</span>
                      <FlagBadge flag={r.flag} />
                      <MissingConfirmationBadge row={r} trackingStart={day.trackingStart} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{METHOD_LABEL[r.method]}</td>
                  <td className="px-4 py-3">
                    <AmountCell row={r} />
                  </td>
                  <td className="px-4 py-3">
                    <RowActions row={r} onResend={onResend} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="divide-y divide-border/50 md:hidden">
          {rows.map((r) => (
            <li key={rowKey(r)} className="flex gap-3 p-4">
              <div className="pt-0.5">
                <FiscalCheck row={r} onToggle={onToggle} />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-foreground">{r.buyer}</div>
                  <AmountCell row={r} />
                </div>
                <div className="text-xs text-muted-foreground">
                  <MetaLine row={r} />
                </div>
                <div className="truncate text-xs text-muted-foreground">{r.description}</div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <span>{r.number}</span>
                  <FlagBadge flag={r.flag} />
                      <MissingConfirmationBadge row={r} trackingStart={day.trackingStart} />
                </div>
                <RowActions row={r} onResend={onResend} />
              </div>
            </li>
          ))}
        </ul>
        <Summary day={day} />
      </div>
    </div>
  );
}
